/**
 * Topology walkers — structured traversal of mesh edges, loops, and islands.
 *
 * Modeled after Blender's BMWalker system: typed walkers that traverse
 * the topology graph using the CSR adjacency cache for O(1) lookups.
 * Each walker supports delimiter flags to stop at sharp edges, seams,
 * or boundary edges.
 *
 * All walkers operate on edge indices (into PolygonTopology.edges) and
 * face indices, not vertex pairs.
 */

import type { AdjacencyCache } from "./topology.ts";
import {
  buildEdgesAndCornerEdges,
  edgeFaces,
  findEdgeIndex,
  getAdjacencyCache,
  numEdgeKey,
  vertexEdges,
} from "./topology.ts";
import type { PolygonTopology } from "./types.ts";
import { EDGE_BOUNDARY, EDGE_SEAM, EDGE_SHARP, EDGE_WIRE } from "./types.ts";
import { buildWeldReps } from "./weld.ts";

// ---------------------------------------------------------------------------
// Delimiter flags
// ---------------------------------------------------------------------------

/** Stop walk at sharp edges. */
export const DELIMIT_SHARP = 0x01;
/** Stop walk at UV seam edges. */
export const DELIMIT_SEAM = 0x02;
/** Stop walk at boundary edges (1 adjacent face). */
export const DELIMIT_BOUNDARY = 0x04;

// ---------------------------------------------------------------------------
// Walk result
// ---------------------------------------------------------------------------

export interface WalkResult {
  /** Edge indices in walk order. */
  edges: number[];
  /** Unique vertex indices on the walk. */
  vertices: number[];
  /** Face indices traversed between consecutive ring/loop edges. */
  faces: number[];
  /** Whether the walk forms a closed loop. */
  closed: boolean;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Check whether an edge should stop the walk based on delimiter flags. */
function isDelimited(
  edgeFlags: Uint8Array,
  edgeIdx: number,
  delimiters: number,
): boolean {
  if (delimiters === 0) return false;
  const flags = edgeFlags[edgeIdx];
  if (delimiters & DELIMIT_SHARP && flags & EDGE_SHARP) return true;
  if (delimiters & DELIMIT_SEAM && flags & EDGE_SEAM) return true;
  if (delimiters & DELIMIT_BOUNDARY && flags & EDGE_BOUNDARY) return true;
  return false;
}

/** Get the other vertex of an edge. */
function otherVert(edges: Uint32Array, edgeIdx: number, v: number): number {
  const v0 = edges[edgeIdx * 2],
    v1 = edges[edgeIdx * 2 + 1];
  return v0 === v ? v1 : v0;
}

/** Get the face shared between two edges, or -1. */
function sharedFace(cache: AdjacencyCache, e1: number, e2: number): number {
  const faces1 = edgeFaces(cache, e1);
  const faces2 = edgeFaces(cache, e2);
  for (let i = 0; i < faces1.length; i++) {
    for (let j = 0; j < faces2.length; j++) {
      if (faces1[i] === faces2[j]) return faces1[i];
    }
  }
  return -1;
}

/**
 * Find the opposite edge in a face relative to a given edge.
 * For quads: the edge 2 corners away. For even-sided n-gons: the edge n/2 corners away.
 * Returns -1 for odd-sided faces or if the edge is not in the face.
 */
function oppositeEdgeInFace(
  topo: PolygonTopology,
  faceIdx: number,
  entryEdge: number,
): number {
  const start = topo.offsets[faceIdx];
  const size = topo.offsets[faceIdx + 1] - start;
  if (size % 2 !== 0) return -1; // odd-sided — no opposite
  const half = size / 2;

  // Find which corner position the entry edge occupies
  for (let j = 0; j < size; j++) {
    if (topo.cornerEdges[start + j] === entryEdge) {
      // The opposite edge is half steps around the face
      return topo.cornerEdges[start + ((j + half) % size)];
    }
  }
  return -1;
}

/**
 * At vertex `tip`, find the continuation edge for edge loop walking.
 *
 * Blender's algorithm (BMW_EDGELOOP): the continuation edge is the one
 * at `tip` that shares NO face with the entry edge. This follows the
 * "perpendicular to ring" direction — latitude lines on a sphere,
 * horizontal loops on a cylinder, etc.
 *
 * When multiple candidates exist (vertex valence > 4), prefer the one
 * with exactly 4 edges at the vertex (standard grid topology).
 *
 * Falls back to n-gon hub traversal: if the vertex has exactly 3 edges
 * and one adjacent face is an n-gon (5+ sides), traverse through the
 * n-gon to find the exit edge on the other side.
 */
function loopStepAtVertex(
  topo: PolygonTopology,
  cache: AdjacencyCache,
  entryEdge: number,
  tip: number,
  visitedEdges: Set<number>,
): { nextEdge: number; face: number } | null {
  const tipEdges = vertexEdges(cache, tip);
  const entryFaces = edgeFaces(cache, entryEdge);

  // Primary: find edge at tip sharing NO face with entry edge
  let candidate = -1;
  let candidateFace = -1;
  for (let i = 0; i < tipEdges.length; i++) {
    const ei = tipEdges[i];
    if (ei === entryEdge || visitedEdges.has(ei)) continue;
    if (topo.edgeFlags[ei] & EDGE_WIRE) continue;

    let shares = false;
    const eiFaces = edgeFaces(cache, ei);
    for (let j = 0; j < entryFaces.length && !shares; j++) {
      for (let k = 0; k < eiFaces.length && !shares; k++) {
        if (entryFaces[j] === eiFaces[k]) shares = true;
      }
    }
    if (!shares) {
      // Found an opposite edge — prefer it if vertex is 4-valent (standard grid)
      if (tipEdges.length === 4) {
        // Find the face between entry and candidate to report as traversed
        // The traversed face is one that touches the entry edge at tip
        for (let j = 0; j < entryFaces.length; j++) {
          const f = entryFaces[j];
          // Check if this face also touches the candidate edge indirectly
          // (they're on the same side of tip)
          candidateFace = f;
        }
        return { nextEdge: ei, face: candidateFace };
      }
      if (candidate < 0) {
        candidate = ei;
        // Pick any face adjacent to the entry edge as the traversed face
        candidateFace = entryFaces.length > 0 ? entryFaces[0] : -1;
      }
    }
  }
  if (candidate >= 0) {
    return { nextEdge: candidate, face: candidateFace };
  }

  // N-gon hub fallback (Blender's f_hub logic):
  // If vertex has exactly 3 edges and 3 faces, and one face is an n-gon (5+ sides),
  // use it as a hub to navigate through.
  if (tipEdges.length === 3) {
    const tipFaceSet = new Set<number>();
    for (let i = 0; i < tipEdges.length; i++) {
      const faces = edgeFaces(cache, tipEdges[i]);
      for (let j = 0; j < faces.length; j++) tipFaceSet.add(faces[j]);
    }
    if (tipFaceSet.size === 3) {
      let hubFace = -1,
        hubLen = 0;
      for (const f of tipFaceSet) {
        const len = topo.offsets[f + 1] - topo.offsets[f];
        if (len > hubLen) {
          hubLen = len;
          hubFace = f;
        }
      }
      if (hubLen > 4 && hubFace >= 0) {
        // In the hub n-gon, find the edge at tip that isn't the entry edge
        const hStart = topo.offsets[hubFace];
        const hSize = topo.offsets[hubFace + 1] - hStart;
        for (let j = 0; j < hSize; j++) {
          const cei = topo.cornerEdges[hStart + j];
          const cv = topo.corners[hStart + j];
          const cnv = topo.corners[hStart + ((j + 1) % hSize)];
          if (cv !== tip && cnv !== tip) continue;
          if (cei === entryEdge) continue;
          // Found the exit edge at tip within the hub — continuation is
          // the non-hub face sharing this exit edge
          const exitFaces = edgeFaces(cache, cei);
          for (let k = 0; k < exitFaces.length; k++) {
            if (exitFaces[k] !== hubFace && !visitedEdges.has(cei)) {
              return { nextEdge: cei, face: exitFaces[k] };
            }
          }
        }
      }
    }
  }

  return null;
}

/**
 * At vertex `tip`, find the next boundary edge for boundary walking.
 * Fans through faces around the vertex starting from the entry edge,
 * continuing until a boundary edge is found on the opposite side.
 *
 * Follows Blender's BMW_EDGEBOUNDARY pattern.
 */
function boundaryStepAtVertex(
  topo: PolygonTopology,
  cache: AdjacencyCache,
  entryEdge: number,
  tip: number,
  visitedEdges: Set<number>,
): { nextEdge: number; face: number } | null {
  const tipEdges = vertexEdges(cache, tip);

  // Find boundary edges at this vertex (excluding entry)
  for (let i = 0; i < tipEdges.length; i++) {
    const ei = tipEdges[i];
    if (ei === entryEdge || visitedEdges.has(ei)) continue;
    if (topo.edgeFlags[ei] & EDGE_WIRE) continue;
    const faces = edgeFaces(cache, ei);
    if (faces.length === 1) {
      // Found next boundary edge — the traversed face is shared between entry and this edge
      const sf = sharedFace(cache, entryEdge, ei);
      const face = sf >= 0 ? sf : faces[0];
      return { nextEdge: ei, face };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Weld-aware edge lookup
// ---------------------------------------------------------------------------

/**
 * Build a weld-aware edge lookup: given a topology and weld reps,
 * construct a map from weld-canonical edge key to all edge indices
 * that represent the same geometric edge.
 */
function buildWeldEdgeMap(
  topo: PolygonTopology,
  weldReps: Int32Array,
): Map<number, number[]> {
  const { edges } = topo;
  const edgeCount = edges.length / 2;
  const map = new Map<number, number[]>();
  for (let i = 0; i < edgeCount; i++) {
    const wk = numEdgeKey(weldReps[edges[i * 2]], weldReps[edges[i * 2 + 1]]);
    let list = map.get(wk);
    if (!list) {
      list = [];
      map.set(wk, list);
    }
    list.push(i);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Edge ring walker
// ---------------------------------------------------------------------------

/**
 * Walk an edge ring starting from `startEdge`.
 *
 * An edge ring crosses each face to its opposite edge (the edge `faceSize/2`
 * corners away). This is the "Ctrl+R" loop cut direction in Blender.
 *
 * Terminates at boundaries, odd-sided faces, delimiters, or when the ring
 * closes back to the start edge.
 *
 * When `positions` is provided, weld-aware: geometrically coincident vertices
 * are treated as the same vertex for edge connectivity.
 */
export function walkRing(
  topo: PolygonTopology,
  cache: AdjacencyCache,
  startEdge: number,
  delimiters = 0,
  positions?: Float32Array,
): WalkResult {
  const weldReps = positions ? buildWeldReps(positions) : null;
  const weldMap = weldReps ? buildWeldEdgeMap(topo, weldReps) : null;

  // Resolve an edge index to all weld-equivalent edge indices
  const weldEquivalents = (ei: number): number[] => {
    if (!weldReps || !weldMap) return [ei];
    const wk = numEdgeKey(
      weldReps[topo.edges[ei * 2]],
      weldReps[topo.edges[ei * 2 + 1]],
    );
    return weldMap.get(wk) ?? [ei];
  };

  // Get all faces adjacent to an edge (including weld equivalents)
  const allEdgeFaces = (ei: number): number[] => {
    const equivs = weldEquivalents(ei);
    if (equivs.length === 1) {
      const f = edgeFaces(cache, ei);
      return Array.from(f);
    }
    const faces: number[] = [];
    const seen = new Set<number>();
    for (const e of equivs) {
      const f = edgeFaces(cache, e);
      for (let i = 0; i < f.length; i++) {
        if (!seen.has(f[i])) {
          seen.add(f[i]);
          faces.push(f[i]);
        }
      }
    }
    return faces;
  };

  const visitedFaces = new Set<number>();
  let closed = false;

  const walkDirection = (
    entryEdge: number,
    edgeList: number[],
    faceList: number[],
  ) => {
    let currentEdge = entryEdge;
    for (let safety = 0; safety < 10000; safety++) {
      const faces = allEdgeFaces(currentEdge);
      let stepped = false;
      for (const f of faces) {
        if (visitedFaces.has(f)) continue;
        const opposite = oppositeEdgeInFace(topo, f, currentEdge);
        if (opposite < 0) continue; // odd-sided face — stop
        if (isDelimited(topo.edgeFlags, opposite, delimiters)) continue;

        visitedFaces.add(f);
        faceList.push(f);

        // Check if we've closed the ring
        if (opposite === startEdge) {
          closed = true;
          return;
        }
        edgeList.push(opposite);
        currentEdge = opposite;
        stepped = true;
        break;
      }
      if (!stepped) break;
    }
  };

  const forwardEdges: number[] = [];
  const forwardFaces: number[] = [];
  const backwardEdges: number[] = [];
  const backwardFaces: number[] = [];

  walkDirection(startEdge, forwardEdges, forwardFaces);
  if (!closed) {
    walkDirection(startEdge, backwardEdges, backwardFaces);
  }

  // Assemble result: backward (reversed) + start + forward
  const edges: number[] = [];
  const faces: number[] = [];
  for (let i = backwardEdges.length - 1; i >= 0; i--)
    edges.push(backwardEdges[i]);
  edges.push(startEdge);
  for (const e of forwardEdges) edges.push(e);
  for (let i = backwardFaces.length - 1; i >= 0; i--)
    faces.push(backwardFaces[i]);
  for (const f of forwardFaces) faces.push(f);

  // Collect unique vertices
  const vertSet = new Set<number>();
  for (const ei of edges) {
    vertSet.add(topo.edges[ei * 2]);
    vertSet.add(topo.edges[ei * 2 + 1]);
  }

  return { edges, vertices: [...vertSet], faces, closed };
}

// ---------------------------------------------------------------------------
// Edge loop walker
// ---------------------------------------------------------------------------

/**
 * Walk an edge loop starting from `startEdge`.
 *
 * An edge loop follows the "perpendicular to ring" direction: at each vertex,
 * it continues to the edge that shares NO face with the entry edge. This is
 * the "Alt+Click" selection direction in Blender (BMW_EDGELOOP).
 *
 * Handles boundary edges (follows the boundary), n-gon hubs (3-valent vertex
 * with an n-gon face), and wire edges.
 *
 * When `positions` is provided, weld-aware: geometrically coincident vertices
 * are treated as the same vertex for edge connectivity.
 */
export function walkLoop(
  topo: PolygonTopology,
  cache: AdjacencyCache,
  startEdge: number,
  delimiters = 0,
  positions?: Float32Array,
): WalkResult {
  // For weld-aware walking, we need to expand the topology's adjacency
  // to include edges between welded vertices. This is the same approach
  // as Blender uses when edges cross UV seams on spheres.
  let effectiveTopo = topo;
  let effectiveCache = cache;

  if (positions) {
    const weldReps = buildWeldReps(positions);
    // Check if any vertices are actually welded
    let hasWelds = false;
    for (let i = 0; i < weldReps.length; i++) {
      if (weldReps[i] !== i) {
        hasWelds = true;
        break;
      }
    }
    if (hasWelds) {
      // Rebuild topology with welded vertices to unify the adjacency
      const vertexCount = positions.length / 3;
      const newCorners = new Uint32Array(topo.corners.length);
      for (let i = 0; i < topo.corners.length; i++) {
        newCorners[i] = weldReps[topo.corners[i]];
      }
      const {
        edges: wEdges,
        cornerEdges: wCE,
        edgeFlags: wEF,
      } = buildEdgesAndCornerEdges(topo.offsets, newCorners);
      effectiveTopo = {
        offsets: topo.offsets,
        corners: newCorners,
        edges: wEdges,
        cornerEdges: wCE,
        edgeFlags: wEF,
      };
      effectiveCache = getAdjacencyCache(effectiveTopo, vertexCount);
      // Remap startEdge to the welded topology
      const sv0 = weldReps[topo.edges[startEdge * 2]];
      const sv1 = weldReps[topo.edges[startEdge * 2 + 1]];
      const weldedStart = findEdgeIndex(effectiveTopo, sv0, sv1);
      if (weldedStart < 0) {
        return {
          edges: [startEdge],
          vertices: [topo.edges[startEdge * 2], topo.edges[startEdge * 2 + 1]],
          faces: [],
          closed: false,
        };
      }
      startEdge = weldedStart;
    }
  }

  const { edges: topoEdges, edgeFlags } = effectiveTopo;
  const startFaces = edgeFaces(effectiveCache, startEdge);
  const isBoundary = startFaces.length === 1;

  const stepper = isBoundary ? boundaryStepAtVertex : loopStepAtVertex;

  const visitedEdges = new Set<number>();
  visitedEdges.add(startEdge);

  let closed = false;

  const walkDirection = (
    tip: number,
    _prev: number,
    edgeList: number[],
    faceList: number[],
  ) => {
    let currentEntry = startEdge;
    let currentTip = tip;
    for (let safety = 0; safety < 10000; safety++) {
      const step = stepper(
        effectiveTopo,
        effectiveCache,
        currentEntry,
        currentTip,
        visitedEdges,
      );
      if (!step) break;
      if (isDelimited(edgeFlags, step.nextEdge, delimiters)) break;
      if (visitedEdges.has(step.nextEdge)) {
        // Closed loop
        if (step.nextEdge === startEdge) closed = true;
        break;
      }
      visitedEdges.add(step.nextEdge);
      edgeList.push(step.nextEdge);
      if (step.face >= 0) faceList.push(step.face);

      // Advance: tip becomes the other vertex of the new edge
      currentEntry = step.nextEdge;
      currentTip = otherVert(topoEdges, step.nextEdge, currentTip);
    }
  };

  const sv0 = topoEdges[startEdge * 2],
    sv1 = topoEdges[startEdge * 2 + 1];

  const fwdEdges: number[] = [];
  const fwdFaces: number[] = [];
  const bwdEdges: number[] = [];
  const bwdFaces: number[] = [];

  // Walk forward from sv1 (tip)
  walkDirection(sv1, sv0, fwdEdges, fwdFaces);
  // Walk backward from sv0 (tip)
  if (!closed) {
    walkDirection(sv0, sv1, bwdEdges, bwdFaces);
  }

  // If no continuation was found, return just the seed edge
  if (fwdEdges.length === 0 && bwdEdges.length === 0) {
    return {
      edges: [startEdge],
      vertices: [sv0, sv1],
      faces: [],
      closed: false,
    };
  }

  // Assemble result: backward (reversed) + start + forward
  const edges: number[] = [];
  const faces: number[] = [];
  for (let i = bwdEdges.length - 1; i >= 0; i--) edges.push(bwdEdges[i]);
  edges.push(startEdge);
  for (const e of fwdEdges) edges.push(e);
  for (let i = bwdFaces.length - 1; i >= 0; i--) faces.push(bwdFaces[i]);
  for (const f of fwdFaces) faces.push(f);

  // Collect unique vertices
  const vertSet = new Set<number>();
  for (const ei of edges) {
    vertSet.add(topoEdges[ei * 2]);
    vertSet.add(topoEdges[ei * 2 + 1]);
  }

  return { edges, vertices: [...vertSet], faces, closed };
}

// ---------------------------------------------------------------------------
// Boundary walker
// ---------------------------------------------------------------------------

/**
 * Walk the boundary (hole) starting from a boundary edge.
 *
 * Follows boundary edges (edges with exactly 1 adjacent face) around a hole
 * in the mesh. Returns the complete boundary loop.
 */
export function walkBoundary(
  topo: PolygonTopology,
  cache: AdjacencyCache,
  startEdge: number,
): WalkResult {
  if (!(topo.edgeFlags[startEdge] & EDGE_BOUNDARY)) {
    return {
      edges: [startEdge],
      vertices: [topo.edges[startEdge * 2], topo.edges[startEdge * 2 + 1]],
      faces: [],
      closed: false,
    };
  }

  const visitedEdges = new Set<number>();
  visitedEdges.add(startEdge);
  const edges: number[] = [startEdge];
  const faces: number[] = [];
  let closed = false;

  // Walk in one direction from the second vertex of the start edge
  const sv1 = topo.edges[startEdge * 2 + 1];
  let tip = sv1;
  let currentEdge = startEdge;

  for (let safety = 0; safety < 100000; safety++) {
    const tipEdges = vertexEdges(cache, tip);
    let nextEdge = -1;
    for (let i = 0; i < tipEdges.length; i++) {
      const ei = tipEdges[i];
      if (ei === currentEdge) continue;
      if (!(topo.edgeFlags[ei] & EDGE_BOUNDARY)) continue;
      if (visitedEdges.has(ei)) {
        if (ei === startEdge) {
          closed = true;
        }
        continue;
      }
      nextEdge = ei;
      break;
    }
    if (nextEdge < 0) break;

    visitedEdges.add(nextEdge);
    edges.push(nextEdge);

    // Track the face between current and next boundary edges
    const sf = sharedFace(cache, currentEdge, nextEdge);
    if (sf >= 0) faces.push(sf);

    tip = otherVert(topo.edges, nextEdge, tip);
    currentEdge = nextEdge;
  }

  // Collect unique vertices
  const vertSet = new Set<number>();
  for (const ei of edges) {
    vertSet.add(topo.edges[ei * 2]);
    vertSet.add(topo.edges[ei * 2 + 1]);
  }

  return { edges, vertices: [...vertSet], faces, closed };
}

// ---------------------------------------------------------------------------
// Island walker (face flood fill)
// ---------------------------------------------------------------------------

/**
 * Walk a connected face island starting from `startFace`.
 *
 * BFS flood-fill: crosses edges to adjacent faces. When `manifoldOnly` is true,
 * only crosses manifold edges (exactly 2 adjacent faces).
 *
 * Follows Blender's BMW_ISLAND / BMW_ISLAND_MANIFOLD pattern.
 */
export function walkIsland(
  topo: PolygonTopology,
  cache: AdjacencyCache,
  startFace: number,
  manifoldOnly = false,
): number[] {
  const visited = new Set<number>();
  visited.add(startFace);
  const queue = [startFace];
  let head = 0;

  while (head < queue.length) {
    const f = queue[head++];
    const fStart = topo.offsets[f];
    const fSize = topo.offsets[f + 1] - fStart;

    for (let j = 0; j < fSize; j++) {
      const ei = topo.cornerEdges[fStart + j];
      if (topo.edgeFlags[ei] & EDGE_WIRE) continue;

      const adjFaces = edgeFaces(cache, ei);
      if (manifoldOnly && adjFaces.length !== 2) continue;

      for (let k = 0; k < adjFaces.length; k++) {
        const neighbor = adjFaces[k];
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }
  }

  return [...visited];
}
